import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { trackInitiateCheckout, trackPurchase } from "../lib/pixel";

function loadRzpScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${id}`).then((r) => setInv(r.data));
  }, [id]);

  if (!inv) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;

  if (inv.paid) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Navbar />
        <div className="px-6 py-16 max-w-xl mx-auto text-center">
          <div className="font-serif-display text-4xl mb-3">Already published ✓</div>
          <div className="text-stone-600 mb-6">Your invitation is live at <code>/i/{inv.slug}</code></div>
          <button onClick={() => nav(`/i/${inv.slug}`)} className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.3em]">View Public Link</button>
        </div>
      </div>
    );
  }

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/create-order", { invitation_id: inv.id });
      const order = r.data;
      // Hard guard — if backend somehow returns mock or empty key_id, refuse to fall back to a fake demo
      if (order.mock || !order.key_id) {
        toast.error("Payment service is being configured on the server. Please contact +91 9936120357.");
        setBusy(false);
        return;
      }
      trackInitiateCheckout({ value: inv.amount, currency: "INR", content_ids: [inv.id], content_name: inv.title });
      const ok = await loadRzpScript();
      if (!ok) {
        toast.error("Could not load payment gateway. Please check your internet connection and try again.");
        setBusy(false);
        return;
      }
      if (typeof window.Razorpay !== "function") {
        toast.error("Razorpay SDK blocked by your browser. Please disable ad-blocker and try again.");
        setBusy(false);
        return;
      }
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Weddinsite Invitations",
        description: inv.title,
        order_id: order.order_id,
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              invitation_id: inv.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackPurchase(inv.amount, "INR", { content_ids: [inv.id], content_name: inv.title });
            toast.success("Payment confirmed. Invitation is live.");
            nav(`/i/${inv.slug}`);
          } catch {
            toast.error("Verification failed. Please contact +91 9936120357 with your payment ID.");
          }
        },
        modal: {
          ondismiss: () => { setBusy(false); },
        },
        theme: { color: "#1A1918" },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try another method.");
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "";
      const status = err?.response?.status;
      console.error("[Checkout] pay failed:", { detail, status, err });
      toast.error(detail ? `Could not start payment: ${detail}` : (status ? `Could not start payment (${status})` : "Could not start payment. Please check your internet & try again."));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="checkout-page">
      <Navbar />
      <div className="px-6 py-16 max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-3">Final step</div>
        <h1 className="font-serif-display text-5xl font-light mb-10">Pay & Publish.</h1>
        <div className="bg-white border border-stone-200 p-8 space-y-4">
          <div className="flex justify-between">
            <span className="text-stone-600">Project</span>
            <span className="font-medium">{inv.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600">Template</span>
            <span className="font-medium uppercase tracking-[0.15em] text-sm">{inv.template_id.replace(/_/g, " ")}</span>
          </div>
          <div className="h-px bg-stone-200" />
          <div className="flex justify-between items-baseline">
            <span className="text-stone-600">MRP</span>
            <span className="text-stone-400 line-through">₹{(inv.mrp || 4999).toLocaleString("en-IN")}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-emerald-700 text-xs uppercase tracking-[0.2em]">Launch offer</span>
            <span className="text-emerald-700 text-sm">− ₹{((inv.mrp || 4999) - inv.amount).toLocaleString("en-IN")}</span>
          </div>
          <div className="flex justify-between text-2xl pt-2 border-t border-stone-200">
            <span>Total</span>
            <span className="font-serif-display">₹{inv.amount.toLocaleString("en-IN")}</span>
          </div>
          <button onClick={pay} disabled={busy} data-testid="checkout-pay-btn" className="w-full py-4 bg-stone-900 text-white uppercase tracking-[0.3em] text-xs hover:bg-stone-800">
            {busy ? "Processing..." : `Pay ₹${inv.amount} & Publish`}
          </button>
          <div className="text-xs text-stone-500 text-center">Powered by Razorpay · Secure payments · UPI · Cards · Netbanking</div>
        </div>
      </div>
    </div>
  );
}
